import * as services from '@services/bankAccount'
import * as types from '@types'
import { getCustomFields } from '@services/personalInfo'
import { resolveCustomFieldValues } from '@convenia/common-organisms/SystemFields/helpers'

export default {
  state: {
    bankAccount: [
      { id: new Date().getUTCMilliseconds(), files: [] }
    ],
    options: {
      bankAccount: {
        bank: [],
        accountType: []
      }
    },
    customFields: {}
  },

  getters: {
    [types.EMPLOYEE_BANK_OPTIONS]: ({ options = {} }) => {
      const bankAccount = options.bankAccount || {}
      const bankOptions = ((bankAccount || {}).bank || [])
        .map(bank => {
          const { name = '', code = '' } = bank
          return { ...bank, name: `${name} - ${code}` }
        })

      return {
        bankAccount: {
          ...bankAccount,
          bank: bankOptions
        }
      }
    }
  },

  mutations: {
    [types.EMPLOYEE_BANK_OPTIONS]: (state, options) => {
      state.options = { ...options }
    },

    [types.EMPLOYEE_BANK]: (state, data) => {
      state.bankAccount = data.length ? data : state.bankAccount
    },

    [types.EMPLOYEE_ADD_BANK]: (state) => {
      state.bankAccount = [
        ...state.bankAccount,
        { id: new Date().getUTCMilliseconds(), files: [] }
      ]
    },

    [types.EMPLOYEE_SET_BANK]: (state, { data, id, previousId }) => {
      const index = state.bankAccount.findIndex(bank => bank.id === (previousId || id))

      if (index >= 0)
        state.bankAccount.splice(index, 1, { files: [], ...data })
      else
        state.bankAccount.push({ ...data })
    },

    [types.EMPLOYEE_SET_BANK_FILES]: (state, { id, files }) => {
      const bankAccount = state.bankAccount.find(bank => bank.id === id)

      if (!bankAccount)
        return

      bankAccount.files = [ ...files ]
    },

    [types.EMPLOYEE_DELETE_BANK]: (state, bankAccount) => {
      state.bankAccount = state.bankAccount.filter(({ id }) => id !== bankAccount.id)
    },

    [types.EMPLOYEE_BANK_CUSTOM_FIELDS]: (state, data) => {
      state.customFields = { ...data }

      state.bankAccount = resolveCustomFieldValues(
        state.bankAccount,
        state.customFields?.bankAccounts
      )
    }
  },

  actions: {
    [types.EMPLOYEE_BANK_OPTIONS]: async ({ state, commit }) => {
      if (state?.options?.bankAccount?.bank?.length) return [ false, state.options ]
      const [ err, options ] = await services.getOptions()

      if (!err) commit(types.EMPLOYEE_BANK_OPTIONS, options)

      return [ err, options ]
    },

    [types.EMPLOYEE_BANK_CUSTOM_FIELDS]: async ({ commit, getters }) => {
      const [ err, data ] = await getCustomFields(getters)

      if (!err) commit(types.EMPLOYEE_BANK_CUSTOM_FIELDS, data)

      return [ err, data ]
    },

    [types.EMPLOYEE_BANK]: async ({ getters, dispatch, commit }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }
      const [ err, data ] = await services.getBankAccount(params)

      if (!err) commit(types.EMPLOYEE_BANK, data)

      await dispatch(types.EMPLOYEE_BANK_CUSTOM_FIELDS)

      return [ err, data ]
    },

    [types.EMPLOYEE_SET_BANK]: async ({ state, commit, getters }, data) => {
      const { schema } = state.customFields.bankAccounts || {}
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId, schema }
      const isCreating = typeof data?.id !== 'string'

      if (isCreating) {
        const [ err, bankAccount ] = await services.createBankAccount(data, params)

        if (!err) commit(types.EMPLOYEE_SET_BANK, { data: bankAccount, previousId: data?.id })

        return [ err, bankAccount ]
      }

      const [ err ] = await services.updateBankAccount(data, params)

      return [ err, data ]
    },

    [types.EMPLOYEE_SET_BANKS]: async ({ state, dispatch }, bankAccounts) => {
      if (!bankAccounts.length) return [ false, state.bankAccount ]

      const submitted = []
      const errors = []

      const results = await Promise.allSettled((bankAccounts || [])
        .map(data => dispatch(types.EMPLOYEE_SET_BANK, data))
      ).catch(err => errors.push(err))

      results.forEach(({ value: [ err, bank ] = [] }) => err
        ? errors.push(err)
        : submitted.push(bank)
      )

      if (!errors.length) await dispatch(types.EMPLOYEE_BANK)

      return [ errors, submitted ]
    },

    [types.EMPLOYEE_DELETE_BANK]: async ({ getters, commit, state }, bankAccount) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }
      const [ err ] = await services.deleteBankAccount(bankAccount, params)

      if (!err) commit(types.EMPLOYEE_DELETE_BANK, bankAccount)

      const hasNoBankAccounts = !state.bankAccount?.length

      if (hasNoBankAccounts) commit(types.EMPLOYEE_ADD_BANK)

      return [ err, null ]
    },

    [types.EMPLOYEE_BANK_UPLOAD_FILES]: async ({ getters }, { id, files }) => {
      const employeeId = getters.getEmployeeUuid
      const params = { id, files, employeeId }
      const [ err, data ] = await services.uploadBankAccountFiles(params)

      if (err) return [ err, [] ]

      return [ err, data ]
    }
  }
}
